import { getActivityAnswers } from '@/data/services/_deprecated/activityServices';
import { QuizCorrectAnswer } from '@/models/_deprecated/ActivityProgress';
import ActivityBlockTool, {
  AnswerCheckResult,
  QuizProgress,
} from '@/utils/editorjs/activity-block';
import {
  ActivityQuiz,
  adapterActivityTool,
} from '@/utils/editorjs/activity-block/adapter/activityToQuizAdapter';
import { AlternativeRaw } from '@/utils/editorjs/activity-block/model/alternative';
import { QuizRaw } from '@/utils/editorjs/activity-block/model/quiz';
import { useRef } from 'react';

export type OnCheckAnswerFn = (
  awnser: AlternativeRaw,
  quiz: QuizRaw,
) => Promise<AnswerCheckResult | undefined>;

type useActivityToolArgs = {
  onAddActivity?: () => void;
  onSelectActivity?: () => void;
  onEditActivity?: (activityId: number) => void;
  onCheckAnswer?: OnCheckAnswerFn;
  getProgress?: (quiz: QuizRaw) => Promise<QuizProgress | undefined>;
  randomize?: boolean;
};

export const useActivityTool = ({
  onAddActivity,
  onSelectActivity,
  onEditActivity,
  onCheckAnswer,
  getProgress,
  randomize = false,
}: useActivityToolArgs = {}) => {
  const promiseRef = useRef<Promise<QuizRaw>>();
  const resolveRef = useRef<((activity: QuizRaw) => void) | null>(null);

  const handleOnEditActivity = async (quiz: QuizRaw) => {
    onEditActivity?.(quiz.activityId);
    if (!promiseRef.current) {
      promiseRef.current = new Promise(resolve => {
        resolveRef.current = resolve;
      });
    }
    return await promiseRef.current;
  };

  const handleOnAddActivity = async () => {
    onAddActivity?.();
    if (!promiseRef.current) {
      promiseRef.current = new Promise(resolve => {
        resolveRef.current = resolve;
      });
    }
    return await promiseRef.current;
  };

  const handleOnSelectActivity = async () => {
    onSelectActivity?.();
    if (!promiseRef.current) {
      promiseRef.current = new Promise(resolve => {
        resolveRef.current = resolve;
      });
    }
    return await promiseRef.current;
  };

  const clearPromise = () => {
    if (resolveRef.current) {
      resolveRef.current = null;
      promiseRef.current = undefined;
    }
  };

  const setActivity = (activity: ActivityQuiz) => {
    const quiz = adapterActivityTool(activity);
    resolveRef.current?.(quiz);
    clearPromise();
  };

  const getAnswer = async (quiz: QuizRaw) => {
    const activity = await getActivityAnswers(quiz.activityId);
    const quizQuestion = activity.questions?.[0] as QuizCorrectAnswer;
    return quizQuestion.correctAnswer;
  };

  return {
    handleOnAddActivity,
    handleOnSelectActivity,
    setActivity,
    clearPromise,
    tool: {
      class: ActivityBlockTool,
      inlineToolbar: true,
      config: {
        allowRetries: true,
        persist: false,
        randomize,
        callbacks: {
          onCheckAnswer,
          getAnswer,
          onAddActivity: handleOnAddActivity,
          onEditActivity: handleOnEditActivity,
          onSelectActivity: handleOnSelectActivity,
          getProgress,
        },
      },
    },
  };
};
